import React, { ReactElement, useEffect } from 'react'

import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionIcon, createStyles, Title, Tooltip } from '@mantine/core'

import Icon from '../Icon'
import Spinner from '../Spinner'

type TSlidebarProps = {
  title: string
  isOpen: boolean
  children: React.ReactNode
  fromSide?: 'left' | 'right'
  width?: number
  top?: number
  onClose?: () => void
  position?: 'absolute' | 'fixed'
  isLoading?: boolean
  lg?: boolean
  // 'data-testid'?: string
}

const useStyle = createStyles(
  (
    theme,
    {
      isOpen,
      fromSide,
      width,
      top,
      position,
    }: {
      isOpen: boolean
      fromSide: 'left' | 'right'
      width: number
      top: number
      position: 'absolute' | 'fixed'
    },
  ) => ({
    wrapper: {
      position,
      top: `${top}px`,
      [fromSide === 'right' ? 'right' : 'left']: isOpen
        ? '0px'
        : `-${width + 50}px`,
      bottom: 0,
      width: `${width}px`,
      background: 'white',
      zIndex: 1040,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '-5px 0px 10px -0px rgba(0, 0, 0, 0.25)',
      transition: 'all 0.2s ease-out',
      overflowY: 'hidden',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    },
  }),
)

const Slidebar = (props: TSlidebarProps): ReactElement => {
  const {
    title,
    isOpen,
    children,
    fromSide = 'right',
    width = 300,
    top = 0,
    onClose,
    position = 'absolute',
    isLoading = false,
    lg = false,
    ...rest
  } = props

  const { classes, cx } = useStyle({ isOpen, fromSide, width, top, position })

  const close = (): void => {
    if (onClose) {
      onClose()
    }
  }

  const onKeyDown = (e): void => {
    if (e.key === 'Escape' && isOpen) {
      close()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  return (
    <>
      <div
        className={classes.wrapper}
        {...rest}
      >
        <div className={cx(classes.header, lg ? 'px-1 pt-1 mx-1' : '')}>
          {lg ? (
            <>
              <Tooltip
                label={i18n.t(
                  'click_here_or_press_escape_to_close',
                  'Hier klicken oder ESC drücken zum Schließen',
                )}
              >
                <ActionIcon
                  onClick={close}
                  variant="subtle"
                  size="xl"
                  className="mr-2"
                >
                  <Icon
                    icon="fal fa-times"
                    size={1.6}
                  />
                </ActionIcon>
              </Tooltip>
              <h3 className="flex-fill">{title}</h3>
              <div className="">{isLoading && <Spinner size={25} />}</div>
            </>
          ) : (
            <>
              <div className="px-2 pt-2 pb-2">
                <Title order={5}>{title}</Title>
              </div>
              <div className="p-1">
                <Tooltip
                  label={i18n.t(
                    'click_here_or_press_escape_to_close',
                    'Hier klicken oder ESC drücken zum Schließen',
                  )}
                  withinPortal
                  zIndex={10000}
                >
                  <ActionIcon onClick={close}>
                    <FontAwesomeIcon
                      size="lg"
                      icon={faTimes}
                    />
                  </ActionIcon>
                </Tooltip>
              </div>
            </>
          )}
        </div>
        <div className={classes.content}>{children}</div>
      </div>
    </>
  )
}

export default Slidebar
